import { useCallback, useEffect, useRef, useState } from "react";
import { useAuthToken } from "@convex-dev/auth/react";
import { ClipboardList, EyeOff, LockKeyhole, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { RequestNotes } from "./RequestNotes";

const ENDPOINT = "https://academic-giraffe-675.convex.site/staff/appointments";
type Status = "received" | "reviewing" | "completed";
type Lead = {
  id: string; name: string; phone: string; email: string;
  preferredDate?: string; preferredTime?: string; patientType?: string;
  comments?: string; status: Status; createdAt: number;
};
type Page = { page: Lead[]; continueCursor: string; isDone: boolean };
const statuses: Record<Status, string> = { received: "New request", reviewing: "Following up", completed: "Follow-up complete" };
function preferredVisit(row: Lead) {
  const preference = [row.preferredDate, row.preferredTime].filter(Boolean).join(" · ");
  return preference.length > 0 ? preference : "Not provided";
}

// Patient details never enter Yodl messages, attachments, search, AI, browser
// storage, analytics or notifications. The only request is directly to Apex.
export function WebsiteLeads() {
  const token = useAuthToken();
  const [rows, setRows] = useState<Lead[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [done, setDone] = useState(true);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [message, setMessage] = useState("");
  const epoch = useRef(0);
  const active = useRef<AbortController | null>(null);
  const pending = useRef(false);
  const lastActivity = useRef(Date.now());

  const clear = useCallback(() => {
    epoch.current += 1;
    active.current?.abort();
    active.current = null;
    pending.current = false;
    setRows([]); setCursor(null); setDone(true); setBusy(false);
    setHidden(true); setLoaded(false); setMessage("");
  }, []);

  useEffect(() => {
    clear();
    return () => { epoch.current += 1; active.current?.abort(); pending.current = false; };
  }, [token, clear]);
  useEffect(() => {
    const activity = () => { lastActivity.current = Date.now(); };
    const visibility = () => { if (document.visibilityState !== "visible") clear(); };
    const timer = window.setInterval(() => { if (Date.now() - lastActivity.current >= 300_000) clear(); }, 15_000);
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("pagehide", clear);
    window.addEventListener("pointerdown", activity);
    window.addEventListener("keydown", activity);
    window.addEventListener("scroll", activity, true);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("pagehide", clear);
      window.removeEventListener("pointerdown", activity);
      window.removeEventListener("keydown", activity);
      window.removeEventListener("scroll", activity, true);
    };
  }, [clear]);

  async function request(body: object, signal: AbortSignal) {
    const response = await fetch(ENDPOINT, {
      method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(body), cache: "no-store", credentials: "omit", referrerPolicy: "no-referrer", signal,
    });
    if (!response.ok) throw new Error(response.status === 401 || response.status === 403 ? "access" : "unavailable");
    return response.json() as Promise<Page>;
  }

  async function run(operation: "list" | "update", id?: string, status?: Status, older = false) {
    if (pending.current || !token || document.visibilityState !== "visible") return;
    pending.current = true;
    const generation = ++epoch.current;
    const controller = new AbortController();
    active.current = controller;
    const timeout = window.setTimeout(() => { controller.abort(); }, 15_000);
    setBusy(true); setMessage("");
    if (operation === "list" && !older) { setRows([]); setLoaded(false); }
    try {
      const result = await request(operation === "list" ? { operation, cursor: older ? cursor : null } : { operation, id, status }, controller.signal);
      if (generation !== epoch.current || document.hidden) return;
      if (operation === "list") {
        setRows(previous => older ? [...previous, ...result.page.filter(row => !previous.some(existing => existing.id === row.id))] : result.page);
        setCursor(result.continueCursor); setDone(result.isDone); setLoaded(true); setHidden(false);
      } else {
        setRows(previous => previous.map(row => row.id === id ? { ...row, status: status ?? row.status } : row));
        setMessage("Follow-up status saved.");
      }
      lastActivity.current = Date.now();
    } catch (error) {
      if (generation !== epoch.current) return;
      clear();
      setMessage(error instanceof Error && error.message === "access"
        ? "Your access needs to be refreshed. Unlock Yodl, then open Website Leads again."
        : operation === "update" ? "We couldn’t confirm the change. Reopen requests to check the latest status before trying again."
          : "Requests couldn’t be loaded. Please try again.");
    } finally {
      window.clearTimeout(timeout);
      if (generation === epoch.current) { setBusy(false); pending.current = false; active.current = null; }
    }
  }

  return (
    <section aria-label="Website Leads" className="min-h-0 min-w-0 flex-1 overflow-y-auto bg-[var(--canvas)] p-4 text-[var(--text)] md:p-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="mb-2 flex items-center gap-2 text-[var(--accent)]"><ClipboardList size={20} /><span className="text-sm font-semibold">Apex Dermatology</span></div>
            <h2 className="text-2xl font-semibold">Website Leads</h2>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">Appointment requests · newest first</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button style={{ minHeight: 44 }} variant="primary" disabled={busy || !token} onClick={() => { void run("list"); }}><RefreshCw size={16} />{busy ? "Please wait…" : hidden ? "Open requests" : "Refresh"}</Button>
            {!hidden && <Button style={{ minHeight: 44 }} onClick={clear}><EyeOff size={16} />Hide details</Button>}
          </div>
        </div>
        <p className="mb-5 flex items-start gap-2 rounded-lg bg-[var(--accent-soft)] p-3 text-sm"><LockKeyhole className="shrink-0" size={17} /><span>Private to the approved Apex follow-up team. These details are not posted to chat. They hide when you leave the app or after 5 minutes without activity.</span></p>
        {message && <p role="status" className="mb-4 rounded-lg border border-[var(--border)] p-3">{message}</p>}
        {busy && !loaded && <div role="status" className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6">Loading requests…</div>}
        {!busy && hidden && <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6"><h3 className="font-semibold">Your private appointment inbox</h3><p className="mt-2 text-[var(--text-secondary)]">Select Open requests to view them using your Yodl login. No separate password.</p></div>}
        {!hidden && loaded && rows.length === 0 && <p className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6">No appointment requests yet. New requests will appear here.</p>}
        {!hidden && <div className="grid gap-4">{rows.map(row => <article key={row.id} className="min-w-0 rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 md:p-5">
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0"><h3 className="break-words text-lg font-semibold">{row.name}</h3><p className="mt-1 text-sm text-[var(--text-secondary)]">Received {new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(row.createdAt)}</p></div>
            <label className="flex flex-col gap-1 text-sm font-medium">Follow-up status<select aria-label={`Follow-up status for ${row.name}`} className="min-h-11 max-w-full rounded-lg border border-[var(--border)] bg-[var(--surface)] px-3 text-base" value={row.status} disabled={busy} onChange={event => { void run("update", row.id, event.target.value as Status); }}>{Object.entries(statuses).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          </div>
          <dl className="grid gap-4 text-base sm:grid-cols-2">
            <div><dt className="text-sm text-[var(--text-secondary)]">Phone</dt><dd className="mt-1 break-words">{row.phone || "Not provided"}</dd></div>
            <div><dt className="text-sm text-[var(--text-secondary)]">Email</dt><dd className="mt-1 break-all">{row.email || "Not provided"}</dd></div>
            <div><dt className="text-sm text-[var(--text-secondary)]">Requested date & time</dt><dd className="mt-1 break-words">{preferredVisit(row)}</dd></div>
            <div><dt className="text-sm text-[var(--text-secondary)]">Patient</dt><dd className="mt-1 break-words">{row.patientType ?? "Not provided"}</dd></div>
            {row.comments && <div className="sm:col-span-2"><dt className="text-sm text-[var(--text-secondary)]">Message</dt><dd className="mt-1 whitespace-pre-wrap break-words">{row.comments}</dd></div>}
          </dl>
          <RequestNotes requestId={row.id} token={token ?? ""} onAccessLost={() => { clear(); setMessage("Your access needs to be refreshed. Unlock Yodl, then open Website Leads again."); }} />
        </article>)}</div>}
        {!hidden && !done && <Button style={{ minHeight: 44 }} className="mt-4" disabled={busy} onClick={() => { void run("list", undefined, undefined, true); }}>Older requests</Button>}
        <p className="mt-5 text-sm text-[var(--text-secondary)]">Requests are not confirmed appointments. Contact the patient to arrange their visit.</p>
      </div>
    </section>
  );
}
